import { Search } from '@mui/icons-material';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	OutlinedInput,
	Typography,
	useTheme
} from '@mui/material';
import { fuzzyInList, fuzzySearch } from '@utils/strings';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { IFlowScreens, Task } from 'types/flows';
import { ITheme } from 'types/theme';

interface IFlowViewAddTaskDialogProps {
	title: string;
	onDone: (id: string, e: React.MouseEvent) => void;
	onSnippetDone: (id: string, e: React.MouseEvent) => void;
	onCancel: () => void;
	snippets?: IFlowSnippet[];
	options?: IFlowNode[];
}

const searchOption = (searchValue: string, option: IFlowNode): boolean =>
	!searchValue ||
	fuzzySearch(option.title, searchValue) ||
	fuzzySearch(option.description, searchValue) ||
	fuzzyInList(option.tags, searchValue);

const FlowViewAddTaskDialog = ({
	title,
	options,
	onDone,
	snippets,
	onSnippetDone,
	onCancel
}: IFlowViewAddTaskDialogProps) => {
	const theme = useTheme<ITheme>();

	const onSelect = useCallback(
		(id: string) => (e: React.MouseEvent) => {
			onDone(id, e);
		},
		[onDone]
	);

	const onSnippetSelect = useCallback(
		(id: string) => (e: React.MouseEvent) => {
			onSnippetDone(id, e);
		},
		[onSnippetDone]
	);

	const [searchValue, setSearchValue] = useState('');

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearchValue(e.target.value);
		},
		[setSearchValue]
	);

	const [readyOptions, setReadyOptions] = useState(options);
	const [readySnippets, setReadySnippets] = useState(snippets);

	useEffect(() => {
		setReadyOptions(
			options?.filter(
				(option) => !option.hidden && searchOption(searchValue, option)
			)
		);
		setReadySnippets(
			snippets?.filter((option) => searchOption(searchValue, option))
		);
	}, [options, setReadyOptions, snippets, setReadySnippets, searchValue]);

	return (
		<Dialog
			data-testid="approval-dialog"
			disableRestoreFocus
			open
			onClose={onCancel}
			maxWidth="sm"
		>
			<DialogTitle>
				<Box
					display="flex"
					flexDirection="row"
					data-testid="flow-add-task-header"
				>
					<Typography variant="h6">
						{intl.tr(`flows.builder.add.${title}`)}
					</Typography>
				</Box>
				<FormControl sx={{ mt: 2, width: '100%' }} variant="outlined">
					<OutlinedInput
						id="outlined-adornment-search"
						inputProps={{ 'data-testid': 'search-actions-input' }}
						value={searchValue}
						onChange={onChange}
						size="small"
						autoFocus
						startAdornment={
							<InputAdornment position="start">
								<Search />
							</InputAdornment>
						}
						placeholder="Search"
					/>
				</FormControl>
			</DialogTitle>
			<DialogContent sx={{ height: 500 }}>
				<List
					sx={{
						width: '100%',
						pb: 0
					}}
				>
					{readySnippets?.map((option) => (
						<ListItem sx={{ p: 0, backgroundColor: 'inherit' }} key={option.id}>
							<ListItemButton
								onClick={onSnippetSelect(option.id)}
								data-testid="flow-snippet-card"
							>
								<ListItemText
									primary={option.title}
									secondary={option.description}
									primaryTypographyProps={{
										color: theme.palette.primary.main,
										variant: 'h6'
									}}
								/>
							</ListItemButton>
						</ListItem>
					))}
				</List>
				<List sx={{ width: '100%', pt: 0 }}>
					{readyOptions?.map((option) => (
						<ListItem sx={{ p: 0, backgroundColor: 'inherit' }} key={option.id}>
							<ListItemButton
								onClick={onSelect(option.id)}
								data-testid="flow-item-card"
							>
								<ListItemText
									primary={option.title}
									secondary={option.description}
									primaryTypographyProps={{
										color: theme.palette.primary.main,
										variant: 'h6'
									}}
								/>
							</ListItemButton>
						</ListItem>
					))}
				</List>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onCancel}
					data-testid="flow-view-add-node-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

FlowViewAddTaskDialog.defaultProps = {
	options: [],
	snippets: []
};

export interface IFlowSnippet extends Omit<IFlowNode, 'hidden'> {
	tasks: Task[];
	screens?: IFlowScreens;
}

export interface IFlowNode {
	title: string;
	description?: string;
	id: string;
	hidden?: boolean;
	tags?: string[];
}

export default FlowViewAddTaskDialog;
